import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { bulkUpdateStudentGroups, getStudentGroup, getStudentGroups } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { FrequentTimeZones, partitionBy } from '@airreading/common';
import {
  EditStudentGroupBulkActions,
  StudentGroupStatus,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

const formSchema = z.object({});

export interface BulkUpdateStudentGroupsProps {
  studentGroups: StudentGroupWithScheduleAndLevel[];
  bulkType: typeof EditStudentGroupBulkActions.ArchiveSessions;
  title: string;
  confirmMessage: string;
  invalidMessage?: string;
  onSuccess?: () => void;
  trigger?: ReactNode;
}

function getBulkContext(studentGroups: StudentGroupWithScheduleAndLevel[]) {
  const firstGroup = studentGroups[0];
  const timeZone = firstGroup?.classTime.schedule.school.org.timeZone ?? FrequentTimeZones.UsCentral;
  const endPlainDate = studentGroups
    .map((sg) => sg.overrideEndPlainDate ?? sg.classTime.schedule.endPlainDate)
    .filter(Boolean)
    .sort((a, b) => Temporal.PlainDate.compare(b, a))[0];

  return {
    timeZone,
    endPlainDate: endPlainDate ?? Temporal.Now.plainDateISO(),
  };
}

export const BulkUpdateStudentGroups = ({
  studentGroups,
  bulkType,
  title,
  confirmMessage,
  invalidMessage,
  onSuccess,
  trigger = <DropdownMenuItem onSelect={(e) => e.preventDefault()}>{title}</DropdownMenuItem>,
}: BulkUpdateStudentGroupsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({ mutationFn: bulkUpdateStudentGroups });

  const [validStudentGroups] = partitionBy(
    studentGroups,
    (studentGroup) => studentGroup.status !== StudentGroupStatus.archived,
  );
  const validCount = validStudentGroups.length;
  const invalidCount = studentGroups.length - validCount;

  const onSubmit = ({ closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    if (validCount === 0) {
      return;
    }

    const { timeZone, endPlainDate } = getBulkContext(validStudentGroups);
    const studentGroupIds = validStudentGroups.map(({ id }) => id);

    return mutateAsync({
      bulkType,
      studentGroupIds,
      startPlainDate: Temporal.Now.plainDateISO(),
      endPlainDate,
      timeZone,
      status: StudentGroupStatus.archived,
    })
      .then(({ success, errors }) => {
        if (errors?.length) {
          toast({
            title: `${success} student group(s) were updated`,
            description: (
              <ul>
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            ),
            variant: 'destructive',
          });
        } else if (success === 0) {
          toast({ title: 'No student groups were updated' });
        } else {
          toast({ title: `${success} student group(s) were archived` });
        }

        queryClient.invalidateQueries(getStudentGroups());
        for (const id of studentGroupIds) {
          queryClient.invalidateQueries(getStudentGroup(id));
        }

        onSuccess?.();
        closeModal();
      })
      .catch((e) => {
        toast({
          title: 'There was an error. Please try again.',
          description: airServerErrorFrom(e)?.userFacingMessage,
          variant: 'destructive',
        });
      });
  };

  return (
    <FormModal
      modalTitle={title}
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: validCount === 0,
        variant: 'destructive',
      }}
      confirmBtnLabel="Confirm"
    >
      {() => (
        <div className="my-6">
          {validCount === 0 ? (
            <div>None of the selected student groups are eligible. {invalidMessage}</div>
          ) : (
            <>
              {invalidCount > 0 && (
                <div className="mb-4">{`There are ${invalidCount} student group(s) that cannot be eligible. ${invalidMessage}`}</div>
              )}
              {validCount > 0 && <div>{`${confirmMessage} ${validCount} student group(s)?`}</div>}
            </>
          )}
        </div>
      )}
    </FormModal>
  );
};
